import { Children, isValidElement, ReactNode, cloneElement } from 'react';

import { onClickCardParams, RFQ } from '../types';

import { clsx, colorSet } from '@components/Provider';

import RfqAccordion from './RfqAccordion';
import RfqCardProjectInfo from './RfqCardProjectInfo';
import { ProjectStatusLabel } from './RfqCardStatus';

import { makeStyles } from '@material-ui/core';
import { CallBackRef } from '@hooks/useIntersectionObserver';
import { useParams } from 'react-router-dom';
export interface RfqCardStyleProps {
  isPcbDesign: boolean;
}

interface Props extends RFQ {
  children: ReactNode;
  matches: boolean;
  isLast: boolean;
  onClickCard: (params: onClickCardParams) => void;
  callbackRef: CallBackRef<HTMLDivElement>;
  ['data-cy']?: string | null;
}

const RfqAccordionType = (<RfqAccordion />).type;
const RfqCardProjectInfoType = (<RfqCardProjectInfo />).type;
const RfqCardStatusType = ProjectStatusLabel;

const render = (children: ReactNode, type: any) => {
  const childrenToArray = Children.toArray(children);

  return childrenToArray.find(
    (child) => isValidElement(child) && child.type === type
  );
};

const RfqCard = ({
  rfq,
  isLast,
  children,
  onClickCard,
  callbackRef,
  //eslint-disable-next-line
  ['data-cy']: testId
}: Props) => {
  const params = useParams() as { rfqId: string };
  const classes = useStyles();

  const extendedChildren = Children.toArray(children).map((child) => {
    return (
      isValidElement(child) &&
      cloneElement({
        ...child,
        props: {
          ...child.props,
          rfq
        }
      })
    );
  });

  return (
    <div
      data-cy={testId}
      ref={(instance) => {
        if (!instance) return;

        if (isLast) {
          return callbackRef(instance);
        }
      }}
      className={clsx([
        classes.root,
        {
          'selected': rfq?.rfqId === params.rfqId,
          'last-item': isLast
        }
      ])}
    >
      <div
        className={classes.content}
        onClick={() => {
          onClickCard({ rfqId: rfq?.rfqId ?? '', eventType: 'REQUEST' });
        }}
      >
        {render(extendedChildren, RfqCardStatusType)}
        {render(extendedChildren, RfqCardProjectInfoType)}
      </div>
      {render(extendedChildren, RfqAccordionType)}
    </div>
  );
};

export default RfqCard;

const useStyles = makeStyles(
  (theme) => ({
    root: {
      maxWidth: 375,
      backgroundColor: colorSet.gray000,
      [theme.breakpoints.down('md')]: {
        maxWidth: '100%'
      },
      '&.selected': {
        backgroundColor: colorSet.gray200
      },
      '&.last-item': {
        marginBottom: 0
      }
    },
    content: {
      display: 'flex',
      flexDirection: 'column',
      padding: 16,
      gap: 12,
      '&:hover': {
        backgroundColor: colorSet.gray200,
        cursor: 'pointer'
      }
    }
  }),
  {
    classNamePrefix: 'rfq-card'
  }
);
